/**
 * Review Service
 * Handles product and shop reviews (create, list, respond)
 */

import { ProductReview, ShopReview } from '../types';
import { reviewRepository } from '../../data/reviewRepository';
import { partnerRepository } from '../../data/partnerRepository';
import { logger } from '../../infrastructure/logger';

export const reviewService = {
    /**
     * Get product reviews (public)
     */
    async getProductReviews(productId: string): Promise<ProductReview[]> {
        try {
            return await reviewRepository.getProductReviews(productId);
        } catch (error) {
            logger.error('[ReviewService] Error getting product reviews', error);
            return [];
        }
    },

    /**
     * Get shop reviews (public)
     */
    async getShopReviews(shopId: string): Promise<ShopReview[]> {
        try {
            return await reviewRepository.getShopReviews(shopId);
        } catch (error) {
            logger.error('[ReviewService] Error getting shop reviews', error);
            return [];
        }
    },

    /**
     * Create product review
     */
    async createProductReview(review: ProductReview): Promise<{ success: boolean; error?: string }> {
        try {
            await reviewRepository.createProductReview(review);
            return { success: true };
        } catch (error) {
            logger.error('[ReviewService] Error creating product review', error);
            return { success: false, error: "Error al crear la reseña del producto" };
        }
    },

    /**
     * Create shop review
     */
    async createShopReview(review: ShopReview): Promise<{ success: boolean; error?: string }> {
        try {
            await reviewRepository.createShopReview(review);
            return { success: true };
        } catch (error) {
            logger.error('[ReviewService] Error creating shop review', error);
            return { success: false, error: "Error al crear la reseña de la tienda" };
        }
    },

    /**
     * Respond to product review (partner)
     */
    async respondToProductReview(reviewId: string, response: string): Promise<void> {
        try {
            await partnerRepository.respondToProductReview(reviewId, response);
        } catch (error) {
            logger.error('[ReviewService] Error responding to product review', error);
            throw error;
        }
    },

    /**
     * Respond to shop review (partner)
     */
    async respondToShopReview(reviewId: string, response: string): Promise<void> {
        try {
            await partnerRepository.respondToShopReview(reviewId, response);
        } catch (error) {
            logger.error('[ReviewService] Error responding to shop review', error);
            throw error;
        }
    },

    /**
     * Get product reviews by partner (partner dashboard)
     */
    async getProductReviewsByPartner(partnerId: string): Promise<ProductReview[]> {
        try {
            return (await partnerRepository.getProductReviewsByPartner(partnerId)) as ProductReview[];
        } catch (error) {
            logger.error('[ReviewService] Error getting partner product reviews', error);
            return [];
        }
    },

    /**
     * Get shop reviews by partner (partner dashboard)
     */
    async getShopReviewsByPartner(partnerId: string): Promise<ShopReview[]> {
        try {
            return (await partnerRepository.getShopReviewsByPartner(partnerId)) as ShopReview[];
        } catch (error) {
            logger.error('[ReviewService] Error getting partner shop reviews', error);
            return [];
        }
    }
};
